import { useCallback, useEffect, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { reports as reportsApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { Loading, Empty, StatCard, money, dateOnly } from '../components/Common';

const TABS = [
  { key: 'sales', label: 'Sales' },
  { key: 'services', label: 'Services' },
  { key: 'customers', label: 'Customers' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'stores', label: 'Stores' },
  { key: 'payments', label: 'Payments' },
];

/** Default range: the last 30 days, which is what most of these reports assume. */
function defaultRange() {
  const to = new Date();
  const from = new Date(Date.now() - 30 * 24 * 3600 * 1000);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

export default function Reports() {
  const toast = useToast();
  const { isSuperAdmin } = useAuth();

  const [tab, setTab] = useState('sales');
  const [range, setRange] = useState(defaultRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from: range.from, to: range.to };
      const res = await reportsApi[tab](params);
      setData(res.data);
    } catch (err) {
      toast.error('Could not load the report', err.message);
    } finally {
      setLoading(false);
    }
  }, [tab, range, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Reports · UrbanClean'; }, []);

  /** Downloads whatever is on screen as CSV - the usual "send it to accounts" ask. */
  const exportCsv = () => {
    const rows = data?.rows || [];
    if (!rows.length) return toast.error('Nothing to export');

    const keys = Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== 'object');
    const csv = [
      keys.join(','),
      ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `urbanclean-${tab}-${range.from}-to-${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Reports</h1>
          <div className="sub">{dateOnly(range.from)} — {dateOnly(range.to)}</div>
        </div>
        <button className="btn btn-ghost" onClick={exportCsv}>Export CSV</button>
      </div>

      <div className="toolbar">
        {TABS.filter((t) => t.key !== 'stores' || isSuperAdmin).map((t) => (
          <button key={t.key} className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
        <div className="spacer" />
        <input className="input" type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
        <input className="input" type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
      </div>

      {loading ? <Loading label="Building the report" /> : !data ? null : (
        <>
          {tab === 'sales' ? <SalesReport data={data} /> : null}
          {tab === 'services' ? <TableReport rows={data.rows} columns={[
            { key: 'name', label: 'Service' }, { key: 'category', label: 'Category' },
            { key: 'quantity', label: 'Quantity', align: 'center' },
            { key: 'revenue', label: 'Revenue', align: 'right', format: money },
          ]} /> : null}
          {tab === 'customers' ? (
            <>
              <div className="grid grid-4 mb">
                <StatCard tone="blue" label="New customers" value={data.newCustomers} sub="in this period" />
                <StatCard tone="navy" label="Active customers" value={data.rows.length} sub="placed at least one order" />
              </div>
              <TableReport rows={data.rows} columns={[
                { key: 'name', label: 'Customer' }, { key: 'mobile', label: 'Mobile' },
                { key: 'orders', label: 'Orders', align: 'center' },
                { key: 'spent', label: 'Spent', align: 'right', format: money },
                { key: 'lastOrderAt', label: 'Last order', format: dateOnly },
              ]} />
            </>
          ) : null}
          {tab === 'delivery' ? <TableReport rows={data.rows} columns={[
            { key: 'name', label: 'Partner' }, { key: 'employmentType', label: 'Type' },
            { key: 'assigned', label: 'Assigned', align: 'center' },
            { key: 'delivered', label: 'Delivered', align: 'center' },
            { key: 'failed', label: 'Failed', align: 'center' },
            { key: 'distanceKm', label: 'Distance (km)', align: 'right' },
            { key: 'earnings', label: 'Earnings', align: 'right', format: money },
          ]} /> : null}
          {tab === 'stores' ? <TableReport rows={data.rows} columns={[
            { key: 'name', label: 'Store' }, { key: 'code', label: 'Code' }, { key: 'city', label: 'City' },
            { key: 'orders', label: 'Orders', align: 'center' },
            { key: 'revenue', label: 'Revenue', align: 'right', format: money },
          ]} /> : null}
          {tab === 'payments' ? (
            <>
              <div className="grid grid-4 mb">
                <StatCard tone="green" label="Collected" value={money(data.collected)} />
                <StatCard tone="amber" label="Outstanding" value={money(data.outstanding)} />
              </div>
              <TableReport rows={data.rows.map((r) => ({ mode: r._id, count: r.count, amount: r.amount }))} columns={[
                { key: 'mode', label: 'Payment mode' },
                { key: 'count', label: 'Transactions', align: 'center' },
                { key: 'amount', label: 'Amount', align: 'right', format: money },
              ]} />
            </>
          ) : null}
        </>
      )}
    </>
  );
}

function SalesReport({ data }) {
  const chart = data.rows.map((r) => ({ date: r._id.slice(5), revenue: r.revenue, orders: r.orders }));

  return (
    <>
      <div className="grid grid-4 mb">
        <StatCard tone="navy" label="Orders" value={data.totals.orders} />
        <StatCard tone="green" label="Revenue" value={money(data.totals.revenue)} />
        <StatCard tone="amber" label="Discounts given" value={money(data.totals.discount)} />
        <StatCard tone="blue" label="GST collected" value={money(data.totals.gst)} />
      </div>

      <div className="card mb">
        <div className="card-head"><h3>Revenue &amp; order trend</h3></div>
        <div className="card-body" style={{ height: 300 }}>
          {chart.length === 0 ? <Empty title="No sales in this range" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 6, right: 10, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f2" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7a95' }} tickLine={false} axisLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#6b7a95' }} tickLine={false} axisLine={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#6b7a95' }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#3182c8" strokeWidth={2.5} dot={false} name="Revenue" />
                <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#1f3468" strokeWidth={2} dot={false} name="Orders" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <TableReport rows={data.rows.map((r) => ({
        date: r._id, orders: r.orders, revenue: r.revenue, discount: r.discount, gst: r.gst,
      }))} columns={[
        { key: 'date', label: 'Date' },
        { key: 'orders', label: 'Orders', align: 'center' },
        { key: 'revenue', label: 'Revenue', align: 'right', format: money },
        { key: 'discount', label: 'Discount', align: 'right', format: money },
        { key: 'gst', label: 'GST', align: 'right', format: money },
      ]} />
    </>
  );
}

function TableReport({ rows, columns }) {
  if (!rows?.length) return <div className="card"><Empty title="No data for this period" /></div>;

  return (
    <div className="card">
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>{columns.map((c) => <th key={c.key} className={c.align === 'right' ? 'right' : c.align === 'center' ? 'center' : ''}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c.key} className={`${c.align === 'right' ? 'right strong' : c.align === 'center' ? 'center' : ''}`}>
                    {c.format ? c.format(row[c.key]) : (row[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
